import { useEffect } from 'react';
import { useShallow } from 'zustand/shallow';
import * as Sentry from '@sentry/react';
import { message } from '@nula/core-ui';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { apiFetch } from '@/lib/http/client';
import { acceptInviteLink } from '@/lib/workspace/api/api';
import { PENDING_INVITATION_KEY, PENDING_INVITATION_VARIANT } from '@/app/routes/invitation-accept';

// Consume a pending invitation token written by InvitationAccept (entity) or
// JoinByToken (link) when a logged-out user lands on an invitation link.
// Fires once when the user successfully signs in (isAuthenticated flips from false → true).
export function InvitationBridge() {
  const { isAuthenticated } = useAuthStore(useShallow((s) => ({
    isAuthenticated: s.isAuthenticated,
  })));
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingToken = sessionStorage.getItem(PENDING_INVITATION_KEY);
    if (!pendingToken) return;
    const variant = sessionStorage.getItem(PENDING_INVITATION_VARIANT) ?? 'entity';
    sessionStorage.removeItem(PENDING_INVITATION_KEY);
    sessionStorage.removeItem(PENDING_INVITATION_VARIANT);

    if (variant === 'link') {
      acceptInviteLink(pendingToken)
        .then(async ({ workspaceId }) => {
          await useWorkspaceStore.getState().refreshFromServer();
          await useWorkspaceStore.getState().switchToWorkspace(
            workspaceId,
            (path) => navigate({ to: path, replace: true }),
          );
        })
        .catch((err: unknown) => {
          console.warn('[InvitationBridge] Failed to resume link invitation:', err);
          Sentry.captureException(err);
          message.error('Could not join workspace. The link may have expired or been revoked.');
        });
      return;
    }

    interface AcceptResponse { kind: string; resourceId: string; workspaceId: string; permission: string; }
    apiFetch<AcceptResponse>(`/invitations/${pendingToken}/accept`, { method: 'POST' })
      .then((data) => {
        const workspaceId = data.workspaceId || useWorkspaceStore.getState().activeWorkspaceId;
        if (workspaceId) {
          window.location.replace(`/${workspaceId}/${data.resourceId}`);
        }
      })
      .catch((err: unknown) => {
        console.warn('[InvitationBridge] Failed to accept pending invitation:', err);
        Sentry.captureException(err);
      });
  }, [isAuthenticated, navigate]);

  return null;
}
